import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Sparkles, Send, Eraser, Loader2, Crown } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { RequirePlan } from "@/components/app/RequirePlan";
import { useLottery } from "@/hooks/use-lottery-selection";
import { useAiCoach } from "@/hooks/use-ai-coach";
import { useAiQuota } from "@/hooks/use-ai-quota";
import { cn } from "@/lib/utils";

const SUGGESTIONS = [
  "Por que meus jogos quase nunca acertam mais que 3 números?",
  "Compare meu perfil com os últimos sorteios.",
  "Vale a pena jogar 7 dezenas em vez de 6 nesta loteria?",
  "Que dezenas atrasadas eu deveria considerar?",
];

const CoachContent = () => {
  const { selected, config } = useLottery();
  const { messages, send, isStreaming, error, reset, cancel } = useAiCoach();
  const { quota, refresh: refreshQuota } = useAiQuota();
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, isStreaming]);

  // Refresh quota counters after each finished stream.
  useEffect(() => { if (!isStreaming) refreshQuota(); }, [isStreaming, refreshQuota]);

  const submit = () => {
    if (!input.trim() || isStreaming) return;
    send(input, selected);
    setInput("");
  };

  return (
    <div className="mx-auto flex h-[calc(100vh-7rem)] max-w-4xl flex-col gap-4 px-4 py-6 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <h1 className="font-serif text-2xl font-light text-foreground sm:text-3xl">
              Coach <em className="text-primary">IA</em>
            </h1>
            <Badge variant="outline" className="border-primary/40 bg-primary/10 text-[10px] uppercase text-primary">
              <Crown className="mr-1 h-3 w-3" /> Premium
            </Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Análise personalizada do seu histórico cruzado com os últimos 100 sorteios da {config.name}.
          </p>
        </div>
        {messages.length > 0 && (
          <Button variant="ghost" size="sm" onClick={reset} disabled={isStreaming}>
            <Eraser className="mr-1.5 h-3.5 w-3.5" /> Nova conversa
          </Button>
        )}
      </div>

      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden border-lt-border bg-lt-bg2">
        <CardContent className="flex min-h-0 flex-1 flex-col gap-0 p-0">
          <div
            ref={scrollRef}
            className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-6 sm:px-6 [scrollbar-color:hsl(var(--primary)/0.4)_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-primary/40"
          >
            {messages.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center gap-6 py-10 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full border border-primary/30 bg-primary/10">
                  <Sparkles className="h-6 w-6 text-primary" />
                </div>
                <div className="space-y-1">
                  <p className="font-serif text-lg text-foreground">Comece uma pergunta</p>
                  <p className="text-sm text-muted-foreground">
                    O Coach IA enxerga seus jogos salvos e o histórico recente da loteria selecionada.
                  </p>
                </div>
                <div className="grid w-full max-w-xl gap-2 sm:grid-cols-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => { setInput(s); }}
                      className="rounded-lg border border-lt-border bg-background p-3 text-left text-[13px] text-foreground transition-colors hover:border-primary/40 hover:bg-primary/5"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m, i) => (
                <div key={i} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
                  <div
                    className={cn(
                      "max-w-[85%] rounded-2xl px-4 py-3 text-[14px] leading-relaxed",
                      m.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "border border-lt-border bg-background text-foreground",
                    )}
                  >
                    {m.role === "assistant" ? (
                      <div className="prose prose-sm max-w-none text-foreground prose-headings:font-serif prose-strong:text-foreground prose-li:my-0.5">
                        <ReactMarkdown>{m.content || "…"}</ReactMarkdown>
                      </div>
                    ) : (
                      <p className="whitespace-pre-wrap">{m.content}</p>
                    )}
                  </div>
                </div>
              ))
            )}
            {isStreaming && messages[messages.length - 1]?.role !== "assistant" && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Pensando…
              </div>
            )}
            {error && (
              <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {error}
              </div>
            )}
          </div>

          <div className="border-t border-lt-border bg-background/50 p-3">
            <div className="flex items-end gap-2">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Pergunte sobre seus jogos, padrões da loteria ou peça uma análise…"
                className="min-h-[44px] flex-1 resize-none border-lt-border bg-background"
                rows={1}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    submit();
                  }
                }}
                disabled={isStreaming}
              />
              {isStreaming ? (
                <Button onClick={cancel} variant="outline" size="icon" className="h-11 w-11">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </Button>
              ) : (
                <Button onClick={submit} size="icon" className="h-11 w-11" disabled={!input.trim()}>
                  <Send className="h-4 w-4" />
                </Button>
              )}
            </div>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-muted-foreground">
              <span>IA analítica — nunca promete prêmios.</span>
              {quota && (
                <span>
                  Plano <strong className="text-foreground">{quota.plan}</strong> · {Math.max(0, quota.daily_limit - quota.daily_used)}/{quota.daily_limit} restantes hoje
                  {" · "}{Math.max(0, quota.monthly_limit - quota.monthly_used)}/{quota.monthly_limit} no mês
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const Coach = () => (
  <RequirePlan
    tier="premium"
    featureName="Coach IA"
    description="Assistente exclusivo Premium que analisa seu histórico e os últimos sorteios para responder suas dúvidas com números reais."
  >
    <CoachContent />
  </RequirePlan>
);

export default Coach;